import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import ruLocale from "@fullcalendar/core/locales/ru";
import enLocale from "@fullcalendar/core/locales/en-gb";
import { useTranslation } from "react-i18next";
import { getTasks } from "../services/taskService";
import { getMeeting } from "../services/meetingService";
import { getProjects } from "../services/projectService";
import { getFromCache } from "../storage/cacheManager";
import { shouldLoadData } from "../utils/dataLoader";
import { Task, Meeting, Project } from "../types";
import useAcl from "../hooks/useAcl";
import InfoModal from "../components/Modals/InfoModal";
import {
  TaskStatusKey,
  MeetingStatusKey,
  taskStatusColors,
  meetingStatusColors,
  getProjectStatusColor,
} from "../utils/statusColors";
import type {
  TaskData,
  MeetingData,
  ProjectData,
  InfoModalType,
} from "../components/Modals/InfoModal";
import calendarStyles from "../styles/calendarStyles";
import ViewSelectorPopover from "../components/Popovers/ViewSelectorPopover";
import { alpha } from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import "dayjs/locale/en";
import { fromServerToLocal } from "../utils/time";

interface ExtendedMeeting extends Meeting {
  location?: string;
}

type CalendarEvent =
  | (TaskData & {
      type: "task";
      title: string;
      color?: string;
      name: string;
      taskType: string;
      backgroundColor?: string;
      popoverBackgroundColor?: string;
      popoverBorderColor?: string;
      popoverTextColor?: string;
      isStart?: boolean;
      isEnd?: boolean;
      startRaw?: string;
      endRaw?: string;
    })
  | (MeetingData & {
      type: "meeting";
      title: string;
      color?: string;
      name: string;
      backgroundColor?: string;
      popoverBackgroundColor?: string;
      popoverBorderColor?: string;
      popoverTextColor?: string;
      isStart?: boolean;
      isEnd?: boolean;
      startRaw?: string;
      endRaw?: string;
    })
  | {
      type: "project";
      id: string;
      title: string;
      name: string;
      status: string;
      start: string;
      end: string;
      startRaw: string;
      endRaw: string;
      description?: string;
      color: string;
      backgroundColor?: string;
      popoverBackgroundColor?: string;
      popoverBorderColor?: string;
      popoverTextColor?: string;
    };

const Calendar: React.FC = () => {
  const calRef = useRef<FullCalendar | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<InfoModalType>("task");
  const [modalData, setModalData] = useState<
    TaskData | MeetingData | ProjectData
  >();
  const [viewAnchorEl, setViewAnchorEl] = useState<HTMLElement | null>(null);

  const [currentView, setCurrentView] = useState<string>("dayGridMonth");
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { canRead: canReadProjects } = useAcl("Project");

  const getEventColor = (status: string, source: "task" | "meeting") => {
    if (source === "task") {
      const key = status as TaskStatusKey;
      return taskStatusColors[key]?.color || "#ccc";
    } else {
      const key = status as MeetingStatusKey;
      return meetingStatusColors[key]?.color || "#ccc";
    }
  };

  useEffect(() => {
    const transformTasksToEvents = (tasks: Task[]): CalendarEvent[] => {
      const events: CalendarEvent[] = [];

      tasks.forEach((task) => {
        const color = getEventColor(task.status, "task");

        const startLocal = fromServerToLocal(task.dateStart);
        const endLocal = fromServerToLocal(task.dateEnd);

        const startDate = startLocal?.toDate();
        const endDate = endLocal?.toDate();

        if (
          !startDate ||
          !endDate ||
          !dayjs(startDate).isValid() ||
          !dayjs(endDate).isValid()
        ) {
          return;
        }

        const isMultiDay = startDate.toDateString() !== endDate.toDateString();
        const startFmt = dayjs(startDate).format("YYYY-MM-DD HH:mm");
        const endFmt = dayjs(endDate).format("YYYY-MM-DD HH:mm");

        if (isMultiDay) {
          events.push({
            id: `${task.id}-start`,
            type: "task" as const,
            title: `${task.name} `,
            name: task.name,
            taskType: task.group || t("calendar.task"),
            status: task.status,
            assignee: task.assignedUserName || t("calendar.noAssignee"),
            start: startFmt,
            end: startFmt,
            startRaw: startFmt,
            endRaw: endFmt,
            description: task.description || "",
            color,
            backgroundColor: color,
            popoverBackgroundColor: alpha(color, 0.12),
            popoverBorderColor: color,
            popoverTextColor: color,
            isStart: true,
          });

          events.push({
            id: `${task.id}-end`,
            type: "task" as const,
            title: `${task.name} `,
            name: task.name,
            taskType: task.group || t("calendar.task"),
            status: task.status,
            assignee: task.assignedUserName || t("calendar.noAssignee"),
            start: endFmt,
            end: endFmt,
            startRaw: startFmt,
            endRaw: endFmt,
            description: task.description || "",
            color,
            backgroundColor: color,
            popoverBackgroundColor: alpha(color, 0.12),
            popoverBorderColor: color,
            popoverTextColor: color,
            isEnd: true,
          });
        } else {
          events.push({
            id: task.id,
            type: "task" as const,
            title: task.name,
            name: task.name,
            taskType: task.group || t("calendar.task"),
            status: task.status,
            assignee: task.assignedUserName || t("calendar.noAssignee"),
            start: startFmt,
            end: endFmt,
            startRaw: startFmt,
            endRaw: endFmt,
            description: task.description || "",
            color,
            backgroundColor: color,
            popoverBackgroundColor: alpha(color, 0.12),
            popoverBorderColor: color,
            popoverTextColor: color,
          });
        }
      });

      return events;
    };

    const transformProjectsToEvents = (
      projects: Project[],
    ): CalendarEvent[] => {
      const events: CalendarEvent[] = [];

      projects.forEach((project) => {
        if (!project.dateEnd) return;

        const endLocal = fromServerToLocal(project.dateEnd);
        if (!endLocal || !dayjs(endLocal).isValid()) return;

        const endDate = endLocal.toDate();
        const endFmt = dayjs(endDate).format("YYYY-MM-DD");

        let startFmt = endFmt;
        if (project.dateStart) {
          const startLocal = fromServerToLocal(project.dateStart);
          if (startLocal && dayjs(startLocal).isValid()) {
            const startDate = startLocal.toDate();
            startFmt = dayjs(startDate).format("YYYY-MM-DD");
          }
        }

        const color = getProjectStatusColor(project.status || "");

        const startRaw = startFmt ? `${startFmt} 00:00` : "";
        const endRaw = endFmt ? `${endFmt} 00:00` : "";

        events.push({
          id: project.id,
          type: "project",
          title: project.name,
          name: project.name,
          status: project.status || "",
          start: endFmt,
          end: endFmt,
          startRaw: startRaw,
          endRaw: endRaw,
          description: project.description || "",
          color,
          backgroundColor: color,
          popoverBackgroundColor: alpha(color, 0.12),
          popoverBorderColor: color,
          popoverTextColor: color,
        });
      });

      return events;
    };

    const transformMeetingsToEvents = (
      meetings: ExtendedMeeting[],
    ): CalendarEvent[] => {
      const events: CalendarEvent[] = [];

      meetings.forEach((meeting) => {
        const color = getEventColor(meeting.status, "meeting");

        const startLocal = fromServerToLocal(meeting.dateStart);
        const endLocal = fromServerToLocal(meeting.dateEnd);

        const startDate = startLocal?.toDate();
        const endDate = endLocal?.toDate();

        if (
          !startDate ||
          !endDate ||
          !dayjs(startDate).isValid() ||
          !dayjs(endDate).isValid()
        ) {
          return;
        }

        const isMultiDay = startDate.toDateString() !== endDate.toDateString();
        const startFmt = dayjs(startDate).format("YYYY-MM-DD HH:mm");
        const endFmt = dayjs(endDate).format("YYYY-MM-DD HH:mm");

        if (isMultiDay) {
          events.push({
            id: `${meeting.id}-start`,
            type: "meeting" as const,
            title: `${meeting.name} `,
            name: meeting.name,
            status: meeting.status,
            assignee: meeting.assignedUserName || t("calendar.noAssignee"),
            start: startFmt,
            end: startFmt,
            startRaw: startFmt,
            endRaw: endFmt,
            description: meeting.description || "",
            color,
            backgroundColor: color,
            popoverBackgroundColor: alpha(color, 0.12),
            popoverBorderColor: color,
            popoverTextColor: color,
            isStart: true,
          });

          events.push({
            id: `${meeting.id}-end`,
            type: "meeting" as const,
            title: `${meeting.name} `,
            name: meeting.name,
            status: meeting.status,
            assignee: meeting.assignedUserName || t("calendar.noAssignee"),
            start: endFmt,
            end: endFmt,
            startRaw: startFmt,
            endRaw: endFmt,
            description: meeting.description || "",
            color,
            backgroundColor: color,
            popoverBackgroundColor: alpha(color, 0.12),
            popoverBorderColor: color,
            popoverTextColor: color,
            isEnd: true,
          });
        } else {
          events.push({
            id: meeting.id,
            type: "meeting" as const,
            title: meeting.name,
            name: meeting.name,
            status: meeting.status,
            assignee: meeting.assignedUserName || t("calendar.noAssignee"),
            start: startFmt,
            end: endFmt,
            startRaw: startFmt,
            endRaw: endFmt,
            description: meeting.description || "",
            color,
            backgroundColor: color,
            popoverBackgroundColor: alpha(color, 0.12),
            popoverBorderColor: color,
            popoverTextColor: color,
          });
        }
      });

      return events;
    };

    const loadData = async () => {
      try {
        const raw = localStorage.getItem("user");
        const userId = raw ? JSON.parse(raw).id : null;

        if (!userId) {
          console.error(t("calendar.errorNoUserId"));
          return;
        }

        let tasks: Task[] = [];
        let meetings: ExtendedMeeting[] = [];
        let projects: Project[] = [];

        const cachedTasks = await getFromCache<any>("tasks:list");
        const cachedMeetings = await getFromCache<any>("meetings:list");
        const cachedProjects = await getFromCache<any>("projects:list");

        tasks = Array.isArray(cachedTasks) ? cachedTasks : cachedTasks?.list || [];
        meetings = Array.isArray(cachedMeetings)
          ? cachedMeetings
          : cachedMeetings?.list || [];
        projects = Array.isArray(cachedProjects)
          ? cachedProjects
          : cachedProjects?.list || [];

        if (!navigator.onLine) {
          const taskEvents = transformTasksToEvents(tasks);
          const meetingEvents = transformMeetingsToEvents(meetings);
          const projectEvents =
            canReadProjects !== false ? transformProjectsToEvents(projects) : [];
          setEvents([...taskEvents, ...meetingEvents, ...projectEvents]);
          return;
        }

        const needTasks = await shouldLoadData("tasks", null);
        const needMeetings = await shouldLoadData("meetings", null);
        const needProjects =
          canReadProjects !== false ? await shouldLoadData("projects", null) : false;

        if (canReadProjects !== false) {
          const [taskResponse, meetingResponse, projectResponse] =
            await Promise.all([
              needTasks ? getTasks(userId) : Promise.resolve({ list: tasks }),
              needMeetings ? getMeeting(userId) : Promise.resolve({ list: meetings }),
              needProjects
                ? getProjects({ maxSize: 100 })
                : Promise.resolve({ list: projects }),
            ]);
          tasks = Array.isArray(taskResponse)
            ? taskResponse
            : taskResponse?.list || [];
          meetings = Array.isArray(meetingResponse)
            ? meetingResponse
            : meetingResponse?.list || [];
          projects = Array.isArray(projectResponse)
            ? projectResponse
            : projectResponse?.list || [];
        } else {
          const [taskResponse, meetingResponse] = await Promise.all([
            needTasks ? getTasks(userId) : Promise.resolve({ list: tasks }),
            needMeetings ? getMeeting(userId) : Promise.resolve({ list: meetings }),
          ]);
          tasks = Array.isArray(taskResponse)
            ? taskResponse
            : taskResponse?.list || [];
          meetings = Array.isArray(meetingResponse)
            ? meetingResponse
            : meetingResponse?.list || [];
        }

        const taskEvents = transformTasksToEvents(tasks);
        const meetingEvents = transformMeetingsToEvents(meetings);
        const projectEvents =
          canReadProjects !== false ? transformProjectsToEvents(projects) : [];
        setEvents([...taskEvents, ...meetingEvents, ...projectEvents]);
      } catch (error) {
        console.error(t("calendar.errorLoadingData"), error);
      }
    };

    loadData();
  }, [t, canReadProjects]);

  const getCurrentLocale = useCallback(() => {
    const lang = i18n.language || localStorage.getItem("language") || "ru_RU";
    return lang.startsWith("ru") ? ruLocale : enLocale;
  }, [i18n.language]);

  const currentLocale = getCurrentLocale();

  useEffect(() => {
    if (calRef.current) {
      const calendarApi = (calRef.current as any).getApi();
      const locale = getCurrentLocale();
      calendarApi.setOption("locale", locale);
      calendarApi.render();
    }
  }, [i18n.language, getCurrentLocale]);

  const handleEventClick = (arg: any) => {
    arg.jsEvent.preventDefault();
    arg.jsEvent.stopPropagation();

    document
      .querySelectorAll(".fc-more-popover .fc-popover-close")
      .forEach((btn) => {
        (btn as HTMLElement).click();
      });

    if (arg.el && typeof arg.el.blur === "function") {
      arg.el.blur();
    }

    const ev = arg.event;
    const props = ev.extendedProps as CalendarEvent;

    const startStr =
      (props as any).startRaw ||
      (ev.start ? dayjs(ev.start).format("YYYY-MM-DD HH:mm") : "");

    const endStr =
      (props as any).endRaw ||
      (ev.end ? dayjs(ev.end).format("YYYY-MM-DD HH:mm") : "");

    let data: TaskData | MeetingData | ProjectData;

    if (props.type === "project") {
      data = {
        id: ev.id,
        name: props.name,
        status: props.status,
        start: startStr,
        end: endStr,
        description: props.description,
      } as ProjectData;
    } else if (props.type === "task") {
      data = {
        id: ev.id.replace(/-start|-end/, ""),
        name: props.name,
        status: props.status,
        assignee: props.assignee,
        start: startStr,
        end: endStr,
        description: props.description,
      } as TaskData;
    } else {
      data = {
        id: ev.id.replace(/-start|-end/, ""),
        name: props.name,
        status: props.status,
        assignee: props.assignee,
        start: startStr,
        end: endStr,
        description: props.description,
      } as MeetingData;
    }

    setModalType(props.type);
    setModalData(data);
    setModalOpen(true);
  };

  const handleEventDidMount = (info: any) => {
    const eventEl = info.el;

    const popoverBgColor = info.event.extendedProps.popoverBackgroundColor;
    const popoverBorderColor = info.event.extendedProps.popoverBorderColor;
    const popoverTextColor = info.event.extendedProps.popoverTextColor;

    if (popoverBgColor) {
      eventEl.style.backgroundColor = popoverBgColor;
    }
    if (popoverBorderColor) {
      eventEl.style.borderLeft = `3px solid ${popoverBorderColor}`;
    }
    if (popoverTextColor) {
      eventEl.style.color = popoverTextColor;

      const mainEl = eventEl.querySelector(
        ".fc-event-main",
      ) as HTMLElement | null;
      if (mainEl) {
        mainEl.style.color = popoverTextColor;
      }

      const titleEl = eventEl.querySelector(
        ".fc-event-title",
      ) as HTMLElement | null;
      if (titleEl) {
        titleEl.style.color = popoverTextColor;
      }
    }
  };

  const handleViewChange = (view: string) => {
    const api = (calRef.current as any)?.getApi();
    if (api) {
      api.changeView(view);
      setCurrentView(view);
    }
    setViewAnchorEl(null);
  };

  const handleDatesSet = (arg: any) => {
    const titleEl = document.querySelector(".fc-toolbar-title");
    if (!titleEl) return;

    const startDate: Date =
      arg.view.currentStart ?? arg.view.activeStart ?? new Date(arg.start);

    const viewType = arg.view.type;
    const lang = i18n.language || localStorage.getItem("language") || "ru_RU";
    const locale = lang.startsWith("ru") ? "ru" : "en";
    let title = "";

    if (viewType === "dayGridMonth") {
      title = dayjs(startDate).locale(locale).format("MMMM YYYY");
    } else if (viewType === "timeGridWeek") {
      const start = dayjs(startDate).locale(locale);
      const end = start.add(6, "day");
      title = `${start.format("D MMMM")} – ${end.format(
        "D MMMM",
      )}, ${start.format("YYYY")}`;
    } else if (viewType === "timeGridDay") {
      title = dayjs(startDate).locale(locale).format("D MMMM YYYY");
    }

    titleEl.textContent = title;
    setCurrentView(viewType);
  };

  const getViewButtonText = () => {
    return t(
      currentView === "dayGridMonth"
        ? "calendar.month"
        : currentView === "timeGridWeek"
          ? "calendar.week"
          : "calendar.day",
    );
  };

  return (
    <Container
      sx={{
        overflowX: "hidden",
        px: { xs: 0, sm: 2 },
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
        >
          {t("calendar.title")}
        </Typography>
      </Box>

      <Box
        sx={{
          ...calendarStyles,
          fontSize: { xs: "0.8rem", sm: "1rem" },
          width: "100%",
          maxWidth: "100%",
          overflowX: "hidden",
        }}
      >
        <FullCalendar
          ref={calRef}
          key={`calendar-${i18n.language}`}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          timeZone="local"
          locale={currentLocale}
          locales={[ruLocale, enLocale]}
          headerToolbar={{
            left: "prev,next title",
            center: "",
            right: isMobile
              ? "viewSelector"
              : "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          customButtons={
            isMobile
              ? {
                  viewSelector: {
                    text: getViewButtonText(),
                    click: (e) => {
                      const button = e.currentTarget as HTMLElement;
                      setViewAnchorEl(button);
                      if (viewAnchorEl) {
                        button.classList.remove("fc-button-active");
                      } else {
                        button.classList.add("fc-button-active");
                      }
                    },
                  },
                }
              : undefined
          }
          buttonText={{
            prev: "",
            next: "",
            dayGridMonth: t("calendar.month"),
            timeGridWeek: t("calendar.week"),
            timeGridDay: t("calendar.day"),
          }}
          datesSet={handleDatesSet}
          views={{
            timeGridWeek: {
              type: "timeGrid",
              duration: { weeks: 1 },
              buttonText: t("calendar.week"),
            },
            timeGridDay: {
              type: "timeGrid",
              duration: { days: 1 },
              buttonText: t("calendar.day"),
            },
          }}
          height="auto"
          events={events}
          eventDisplay="block"
          dayMaxEventRows={2}
          moreLinkContent={() => t("calendar.more")}
          eventClick={handleEventClick}
          eventDidMount={handleEventDidMount}
          eventTimeFormat={{
            hour: "numeric",
            minute: "2-digit",
            omitZeroMinute: true,
            meridiem: false,
            hour12: false,
          }}
          displayEventTime={true}
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          slotDuration="00:30:00"
          dayHeaderFormat={{
            weekday: isMobile ? "short" : "long",
          }}
          eventContent={(arg) => (
            <Box
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {arg.event.title}
            </Box>
          )}
          contentHeight="auto"
          aspectRatio={1.5}
        />
      </Box>

      <ViewSelectorPopover
        anchorEl={viewAnchorEl}
        onClose={() => {
          setViewAnchorEl(null);
          const button = document.querySelector(
            ".fc-viewSelector-button",
          ) as HTMLElement | null;
          if (button) {
            button.classList.remove("fc-button-active");
          }
        }}
        onViewChange={handleViewChange}
      />

      {modalData && (
        <InfoModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          type={modalType}
          data={modalData}
        />
      )}
    </Container>
  );
};

export default Calendar;
